import React from 'react';
import { Helmet } from 'react-helmet';

export const WebApplicationSchema = ({ aggregateRating = null } = {}) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "UK Take Home Pay Calculator",
        "url": "https://salarytools.co.uk",
        "description": "Calculate your UK take home pay with our interactive calculator. Break down your salary, tax and National Insurance into a clear visual summary.",
        "applicationCategory": "FinanceApplication",
        "operatingSystem": "Any",
        "permissions": "none",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP"
        },
        "featureList": [
          "UK tax calculation",
          "National Insurance calculation", 
          "Take-home pay calculation",
          "Pension contribution calculator",
          "Student loan repayment calculator",
          "Overtime pay calculation",
          "Visual salary breakdown"
        ],
        "author": {
          "@type": "Organization",
          "name": "SalaryTools.co.uk",
          "url": "https://salarytools.co.uk"
        }
      })}
    </script>
  </Helmet>
);

export const ArticleSchema = ({ title, description, datePublished, dateModified, url }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": description,
        "url": url,
        "datePublished": datePublished,
        "dateModified": dateModified,
        "author": {
          "@type": "Organization",
          "name": "SalaryTools.co.uk",
          "url": "https://salarytools.co.uk"
        },
        "publisher": {
          "@type": "Organization",
          "name": "SalaryTools.co.uk",
          "url": "https://salarytools.co.uk"
        }
      })}
    </script>
  </Helmet>
);

export const CalculatorSchema = ({ salary, takeHomePay, taxAmount, nationalInsuranceAmount, aggregateRating = null }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": `${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(salary)} After Tax UK Calculator`,
        "url": `https://salarytools.co.uk/${salary}-after-tax-uk`,
        "description": `Calculate your take-home pay from a ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(salary)} salary in the UK. See exactly how much you'll earn after tax, National Insurance, and other deductions.`,
        "applicationCategory": "FinanceApplication",
        "operatingSystem": "Any",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP"
        },
        "potentialAction": {
          "@type": "UseAction",
          "target": `https://salarytools.co.uk/${salary}-after-tax-uk`,
          "object": {
            "@type": "QuantitativeValue",
            "name": "Annual Salary",
            "value": salary,
            "unitCode": "GBP"
          },
          "result": {
            "@type": "QuantitativeValue", 
            "name": "Take Home Pay",
            "value": takeHomePay,
            "unitCode": "GBP"
          }
        },
        "author": {
          "@type": "Organization",
          "name": "SalaryTools.co.uk",
          "url": "https://salarytools.co.uk"
        }
      })}
    </script>
  </Helmet>
);

export const BreadcrumbSchema = ({ items }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url
        }))
      })}
    </script>
  </Helmet>
);

export const HowToSchema = ({ name, description, steps }) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": name,
        "description": description,
        "step": steps.map((step, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": step.name,
          "text": step.text
        }))
      })}
    </script>
  </Helmet>
);